import { useMemo, type ComponentPropsWithRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useLinkProps,
  useLocation,
  useRouter,
  type UseLinkPropsOptions,
  type RegisteredRouter,
} from "@tanstack/react-router";
import type { LinkRenderer } from "@softmax/design-system";
import {
  FeatureNavigationProvider,
  type FeatureNavigation,
} from "@softmax/web-features/navigation";
import { matchRoute } from "@softmax/web-addresses";
import { parsePageSearch } from "@softmax/web-addresses/search";

function isProductHref(href: string | undefined): href is string {
  if (!href || !href.startsWith("/") || href.startsWith("//")) return false;

  const route = matchRoute(href.replace(/[?#].*$/, ""));
  return (
    (route.bucket === "app" || route.bucket === "unlisted") &&
    (route.disposition === "page" || route.disposition === "resolver")
  );
}

const renderLink: LinkRenderer = (props) => <ProductLink {...props} />;

function ProductLink({ href, children, ...props }: ComponentPropsWithRef<"a">) {
  if (
    (props.download === undefined || props.download === false) &&
    isProductHref(href)
  )
    return (
      <OwnedProductLink {...props} href={href}>
        {children}
      </OwnedProductLink>
    );

  return (
    <a {...props} href={href}>
      {children}
    </a>
  );
}

function OwnedProductLink({
  href,
  target,
  ...props
}: ComponentPropsWithRef<"a"> & { href: string }) {
  const destination = new URL(href, "https://softmax.invalid");
  const options: UseLinkPropsOptions<RegisteredRouter, string, string> = {
    ...props,
    to: destination.pathname,
    search: parsePageSearch(destination.search),
    hash: destination.hash.slice(1),
    preload: "intent",
  };

  if (target !== undefined) options.target = target;

  const linkProps = useLinkProps(options);
  return (
    <a
      {...linkProps}
      aria-current={
        Object.hasOwn(props, "aria-current")
          ? props["aria-current"]
          : linkProps["aria-current"]
      }
    >
      {props.children}
    </a>
  );
}

export function ProductNavigation({ children }: { children: ReactNode }) {
  const router = useRouter();
  const location = useLocation();
  const client = useQueryClient();
  const navigation = useMemo<FeatureNavigation>(
    () => ({
      pathname: location.pathname,
      search: location.searchStr,
      push: ({ href, scroll }) => {
        if (isProductHref(href))
          void router.navigate({
            href,
            resetScroll: scroll !== false,
          });
        else window.location.assign(href);
      },
      replace: ({ href, scroll }) => {
        if (isProductHref(href))
          void router.navigate({
            href,
            replace: true,
            resetScroll: scroll !== false,
          });
        else window.location.replace(href);
      },
      back: () => router.history.back(),
      refresh: () => {
        void client.invalidateQueries();
        void router.invalidate();
      },
    }),
    [location.pathname, location.searchStr, router, client],
  );
  return (
    <FeatureNavigationProvider value={navigation} renderLink={renderLink}>
      {children}
    </FeatureNavigationProvider>
  );
}
